/**
 * POLISH CARD COMPONENT - WITH USAGE TRACKING!
 * 
 * Features:
 * - Interactive emoji toggles at the bottom (PublicWishlist, Favorite, Rare/LE, Needs Info)
 * - Usage meter bar showing how many times polish has been used (hidden for wishlist items)
 * - Collection/Event info display with click-to-filter
 * - Tags display with click-to-filter
 * - Selection mode support for bulk actions
 * - Click to open detail modal
 * - Grayed-out appearance when destashed
 * - Brand category corner ribbon (Indie/Boutique/Mainstream)
 * 
 * Note: Destash and Swap toggles moved to Detail Modal for better UX
 */

import React, { useState, useEffect } from 'react';
import { Package, Sparkles, Eye } from 'lucide-react';
import { AppIcons } from '../../constants/icons';
import { CategoryColors, PriorityColors, type PriorityKey } from '../../constants/categoryColors';
import type { CollectionItem } from '../../types';
import DestashStatusBadge from '../DestashStatusBadge';
import { findKnownCategory, getCategoryDisplay } from '../../types/brandCategories';
import PrivateLockBadge from '../common/PrivateLockBadge';

interface PolishCardProps {
  polish: CollectionItem;
  onUpdate: (id: string, updates: Partial<CollectionItem>) => Promise<void>;
  onClick?: () => void;
  userId: string;
  isSelectionMode?: boolean;
  isSelected?: boolean;
  onSelect?: (polishId: string) => void;
  onFilterByTag?: (tagCategory: string, tagValue: string) => void;
  onFilterByCollection?: (collectionName: string) => void;
  onFilterByEvent?: (eventSource: string) => void;
  brandCategory?: 'indie' | 'boutique' | 'mainstream' | null; // Optional - will auto-detect if not provided
}

const PolishCard: React.FC<PolishCardProps> = ({
  polish,
  onUpdate,
  onClick,
  userId,
  isSelectionMode = false,
  isSelected = false,
  onSelect,
  onFilterByTag,
  onFilterByCollection,
  onFilterByEvent,
  brandCategory: propBrandCategory
}) => {

  // Determine brand category - use prop if provided, otherwise auto-detect
  const brandCategory = propBrandCategory ?? findKnownCategory(polish.brand || '');
  const categoryDisplay = brandCategory ? getCategoryDisplay(brandCategory) : null;

  // Local state for toggles - enables instant visual feedback
  const [onWishlist, setOnWishlist] = useState(polish.onWishlist || false);
  const [isFavorite, setIsFavorite] = useState(polish.isFavorite || false);
  const [isRareLE, setIsRareLE] = useState(polish.isRareLE || false);
  const [needsInfo, setNeedsInfo] = useState(polish.needsInfo || false);
  const [swatched, setSwatched] = useState(polish.swatched || false);

  // Sync local state when props change (e.g., after modal updates)
  useEffect(() => {
    setOnWishlist(polish.onWishlist || false);
    setIsFavorite(polish.isFavorite || false);
    setIsRareLE(polish.isRareLE || false);
    setNeedsInfo(polish.needsInfo || false);
    setSwatched(polish.swatched || false);
  }, [polish.onWishlist, polish.isFavorite, polish.isRareLE, polish.needsInfo, polish.swatched]);

  // Handle emoji toggle clicks - with optimistic update!
  const handleToggle = async (field: 'onWishlist' | 'needsInfo' | 'isFavorite' | 'isRareLE' | 'swatched', e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    if (isSelectionMode) return; // Disabled in selection mode

    // Get current value from local state
    let currentValue = false;
    switch (field) {
      case 'onWishlist': currentValue = onWishlist; break;
      case 'isFavorite': currentValue = isFavorite; break;
      case 'isRareLE': currentValue = isRareLE; break;
      case 'needsInfo': currentValue = needsInfo; break;
      case 'swatched': currentValue = swatched; break;
    }
    const newValue = !currentValue;

    // Update local state immediately (instant visual feedback!)
    switch (field) {
      case 'onWishlist': setOnWishlist(newValue); break;
      case 'isFavorite': setIsFavorite(newValue); break;
      case 'isRareLE': setIsRareLE(newValue); break;
      case 'needsInfo': setNeedsInfo(newValue); break;
      case 'swatched': setSwatched(newValue); break;
    }

    // Save to database - when adding to wishlist, also set default ISO priority
    if (field === 'onWishlist' && newValue) {
      await onUpdate(polish.id, { [field]: newValue, isoPriority: polish.isoPriority || 'iso' });
    } else {
      await onUpdate(polish.id, { [field]: newValue });
    }
  };

  // Handle card click
  const handleCardClick = () => {
    if (isSelectionMode && onSelect) {
      onSelect(polish.id);
    } else if (onClick) {
      onClick();
    }
  };

  // Handle tag click
  const handleTagClick = (category: string, value: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onFilterByTag) {
      onFilterByTag(category, value);
    }
  };

  // Handle collection click
  const handleCollectionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onFilterByCollection && polish.collectionName) {
      onFilterByCollection(polish.collectionName);
    }
  };

  // Handle event click
  const handleEventClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const evt = polish.eventName || polish.eventSource;
    if (onFilterByEvent && evt) {
      onFilterByEvent(evt);
    }
  };

  // Helper to check if a photo is hidden due to DMCA
  const isPhotoHidden = (photo: any): boolean => {
    return typeof photo === 'object' && photo.dmcaStatus === 'hidden';
  };

  // Get first available image - NEW PHOTO SYSTEM (filters DMCA-hidden photos)
  // First try the new photos[] array, then fall back to legacy arrays
  const getDisplayImage = (): string | null => {
    // New system: photos[] array with isDefault flag
    if (polish.photos && polish.photos.length > 0) {
      const defaultPhoto = polish.photos.find(p => p.isDefault && !isPhotoHidden(p));
      if (defaultPhoto) return defaultPhoto.url;
      const visiblePhoto = polish.photos.find(p => !isPhotoHidden(p));
      if (visiblePhoto) return visiblePhoto.url;
    }
    
    // Legacy fallback (in case some items haven't been migrated)
    if (polish.swatchPhotos && polish.swatchPhotos.length > 0) {
      const visible = (polish.swatchPhotos as any[]).find((p: any) => !isPhotoHidden(p));
      if (visible) return typeof visible === 'string' ? visible : visible.url;
    }
    if (polish.bottlePhotos && polish.bottlePhotos.length > 0) {
      const visible = (polish.bottlePhotos as any[]).find((p: any) => !isPhotoHidden(p));
      if (visible) return typeof visible === 'string' ? visible : visible.url;
    }
    if (polish.macroPhotos && polish.macroPhotos.length > 0) {
      const visible = (polish.macroPhotos as any[]).find((p: any) => !isPhotoHidden(p));
      if (visible) return typeof visible === 'string' ? visible : visible.url;
    }
    if (polish.inUsePhotos && polish.inUsePhotos.length > 0) {
      const visible = (polish.inUsePhotos as any[]).find((p: any) => !isPhotoHidden(p));
      if (visible) return typeof visible === 'string' ? visible : visible.url;
    }
    
    return null;
  };
  
  const firstImage = getDisplayImage();

  // Calculate usage level (use local state for isFavorite)
  const timesUsed = polish.timesUsed || 0;
  
  // Usage bar config
  let usageLevel = 'Untried';
  let usagePercentage = 0;
  let usageColor = 'bg-gray-300';
  let usageBarColor = 'bg-gray-400';
  
  if (isFavorite || timesUsed >= 7) {
    usageLevel = 'Fave';
    usagePercentage = 100;
    usageColor = 'bg-yellow-100';
    usageBarColor = 'bg-gradient-to-r from-yellow-400 to-orange-400';
  } else if (timesUsed >= 3) {
    usageLevel = 'Loved';
    usagePercentage = 60;
    usageColor = 'bg-pink-100';
    usageBarColor = 'bg-gradient-to-r from-pink-400 to-fuchsia-400';
  } else if (timesUsed >= 1) {
    usageLevel = 'Tried';
    usagePercentage = 20;
    usageColor = 'bg-purple-100';
    usageBarColor = 'bg-gradient-to-r from-purple-400 to-purple-500';
  }

  // Collect all displayable tags
  const displayTags: { category: string; value: string; label: string }[] = [];
  
  // Base type
  if (polish.baseType) {
    displayTags.push({ category: 'baseType', value: polish.baseType, label: polish.baseType });
  }
  
  // Finish
  if (polish.finish && polish.finish.length > 0) {
    polish.finish.forEach((f: string) => {
      displayTags.push({ category: 'finish', value: f, label: f });
    });
  }
  
  // Holo type
  if (polish.holoType && polish.holoType.length > 0) {
    polish.holoType.forEach((h: string) => {
      displayTags.push({ category: 'holoType', value: h, label: h });
    });
  }
  
  // Color shift
  if (polish.colorShift) {
    displayTags.push({ category: 'colorShift', value: polish.colorShift, label: polish.colorShift });
  }
  
  // Particles
  if (polish.particles && polish.particles.length > 0) {
    polish.particles.forEach((p: string) => {
      displayTags.push({ category: 'particles', value: p, label: p });
    });
  }
  
  // Special features
  if (polish.specialFeatures && polish.specialFeatures.length > 0) {
    polish.specialFeatures.forEach((s: string) => {
      displayTags.push({ category: 'specialFeatures', value: s, label: s });
    });
  }

  // Get tag color based on category
  const getTagColor = (category: string): string => {
    switch (category) {
      case 'baseType':
        return 'bg-blue-600/80 hover:bg-blue-600';
      case 'finish':
        return 'bg-purple-600/80 hover:bg-purple-600';
      case 'holoType':
        return 'bg-pink-600/80 hover:bg-pink-600';
      case 'colorShift':
        return 'bg-indigo-600/80 hover:bg-indigo-600';
      case 'particles':
        return 'bg-amber-600/80 hover:bg-amber-600';
      case 'specialFeatures':
        return 'bg-green-600/80 hover:bg-green-600';
      default:
        return 'bg-slate-600/80 hover:bg-slate-600';
    }
  };

  // Check if we have collection/event info to display
  const hasCollectionInfo = polish.collectionName && polish.collectionName.trim() !== '';
  const _eventDisplay = (polish.eventName || polish.eventSource || '').trim();
  const hasEventInfo = _eventDisplay !== '';
  const hasReleaseInfo = polish.releaseSeason || polish.releaseYear || polish.releaseMonth;
  
  // Helper to format release date display - shows month if available, season as fallback
  const getReleaseDateDisplay = () => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthToSeason: Record<number, string> = {
      1: 'Winter', 2: 'Winter', 3: 'Spring', 4: 'Spring', 5: 'Spring',
      6: 'Summer', 7: 'Summer', 8: 'Summer', 9: 'Fall', 10: 'Fall', 11: 'Fall', 12: 'Winter'
    };
    
    // Determine effective season (from month if available, otherwise from releaseSeason)
    const effectiveSeason = polish.releaseMonth 
      ? monthToSeason[polish.releaseMonth] 
      : polish.releaseSeason;
    
    // Build display text: prefer month over season (more specific)
    let text = '';
    if (polish.releaseMonth) {
      text = monthNames[polish.releaseMonth - 1];
    } else if (polish.releaseSeason) {
      text = polish.releaseSeason;
    }
    if (polish.releaseYear) {
      text = text ? `${text} ${polish.releaseYear}` : `${polish.releaseYear}`;
    }
    
    return { text, effectiveSeason };
  };
  
  // Release type display helper
  const getReleaseTypeDisplay = () => {
    // Don't show badge for unknown, event, or collection (they show their own specific badges)
    if (!polish.releaseType || polish.releaseType === 'unknown' || polish.releaseType === 'event' || polish.releaseType === 'collection') return null;
    const typeMap: Record<string, { emoji: string; label: string; color: string }> = {
      core: { emoji: '🏠', label: 'Core', color: 'bg-gray-100 text-gray-700' },
      box: { emoji: '📦', label: 'Box', color: 'bg-amber-50 text-amber-700' },
      groupCustom: { emoji: '👥', label: 'Group', color: 'bg-cyan-50 text-cyan-700' },
      exclusive: { emoji: '⭐', label: 'Exclusive', color: 'bg-yellow-50 text-yellow-700' },
      charity: { emoji: '💝', label: 'Charity', color: 'bg-pink-50 text-pink-700' },
      limitedEdition: { emoji: '✨', label: 'LE', color: 'bg-rose-50 text-rose-700' },
      convention: { emoji: '🎫', label: 'Convention', color: 'bg-teal-50 text-teal-700' },
      collab: { emoji: '🤝', label: 'Collab', color: 'bg-violet-50 text-violet-700' },
      other: { emoji: '📝', label: 'Other', color: 'bg-gray-50 text-gray-600' },
    };
    return typeMap[polish.releaseType] || null;
  };
  
  const releaseTypeDisplay = getReleaseTypeDisplay();

  return (
    <div 
      onClick={handleCardClick}
      className={`card-light overflow-hidden hover:shadow-2xl transform hover:-translate-y-1 transition-all cursor-pointer flex flex-col ${
        isSelectionMode && isSelected ? 'ring-4 ring-blue-500' : ''
      } ${polish.isDestashed ? 'opacity-100 grayscale' : ''}`}
    >
      {/* Photo with Selection Overlay */}
      <div className="relative">
        {firstImage ? (
          <img
            src={firstImage}
            alt={`${polish.brand} ${polish.name}`}
            className="w-full h-64 object-cover"
          />
        ) : (
          <div className="w-full h-64 rainbow-gradient flex items-center justify-center">
            <AppIcons.Polish className="w-16 h-16 text-white" aria-label="Nail Polish" />
          </div>
        )}

        {/* Selection Overlay & Checkbox */}
        {isSelectionMode && (
          <>
            {isSelected && (
              <div className="absolute inset-0 bg-blue-500 bg-opacity-30 flex items-center justify-center">
                <div className="w-20 h-20 bg-blue-500 rounded-full flex items-center justify-center">
                  <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
            )}
            <div className="absolute top-2 left-2">
              <div className={`w-6 h-6 rounded border-2 flex items-center justify-center ${
                isSelected ? 'bg-blue-500 border-blue-500' : 'bg-white border-gray-400'
              }`}>
                {isSelected && (
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
            </div>
          </>
        )}

        {/* Polish Type Badge */}
        <div className="absolute top-2 right-2">
          <span className={`px-2 py-1 ${CategoryColors.polish.badge} rounded-full text-xs font-bold font-body`}>
            {polish.category || 'Polish'}
          </span>
        </div>

        {/* Private lock — owner-only marker; suppressed in selection mode
            so it doesn't fight the selection checkbox in the top-left. */}
        {!isSelectionMode && polish.isPrivate && <PrivateLockBadge />}

        {/* ISO Priority Badge - Shows when on wishlist - Centered to avoid overlap with corner ribbon */}
        {!isSelectionMode && polish.onWishlist && (
          <div className="absolute top-2 left-1/2 -translate-x-1/2">
            <span className={`px-2 py-1 rounded-full text-xs font-bold text-white font-body shadow-md ${
              PriorityColors[polish.isoPriority as PriorityKey]?.bg || PriorityColors.iso.bg
            }`}>
              {PriorityColors[polish.isoPriority as PriorityKey]?.label || PriorityColors.iso.label}
            </span>
          </div>
        )}

        {/* Brand Category Corner Ribbon */}
        {categoryDisplay && brandCategory !== 'unknown' && (
          <div className="absolute top-0 left-0 overflow-hidden w-28 h-28 pointer-events-none">
            <div className={`absolute top-5 -left-10 w-36 transform rotate-[-45deg] text-center py-1 text-xs font-bold shadow-lg ${
              brandCategory === 'indie'
                ? 'bg-gradient-to-r from-purple-700 to-purple-600 text-white'
                : brandCategory === 'boutique'
                ? 'bg-gradient-to-r from-pink-600 to-pink-500 text-white'
                : 'bg-gradient-to-r from-blue-600 to-blue-500 text-white'
            }`}>
              {categoryDisplay.emoji} {categoryDisplay.label}
            </div>
          </div>
        )}
      </div>
      
      {/* Info - Using flex-1 to push bottom content down */}
      <div className="p-4 flex flex-col flex-1">
        {/* Top Section - Brand & Name */}
        <div className="mb-2">
          <h3 className={`text-xs uppercase ${CategoryColors.polish.text} font-bold tracking-wide mb-1 font-heading`}>
            {polish.brand}
          </h3>
          
          <h4 className="text-lg font-bold text-light-primary leading-tight font-body">
            {polish.name}
          </h4>
        </div>

        {/* Color Description */}
        {polish.colorDescription && (
          <p className="text-sm text-light-secondary mb-2 line-clamp-2 font-body">
            {polish.colorDescription}
          </p>
        )}

        {/* Collection/Event Info - Clickable */}
        {(releaseTypeDisplay || hasCollectionInfo || hasEventInfo || hasReleaseInfo) && (
          <div className="flex flex-wrap items-center gap-1.5 mb-2">
            {/* Release Type Badge */}
            {releaseTypeDisplay && (
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${releaseTypeDisplay.color}`}
                title={`Release Type: ${releaseTypeDisplay.label}`}
              >
                <span>{releaseTypeDisplay.emoji}</span>
                <span>{releaseTypeDisplay.label}</span>
              </span>
            )}
            {hasCollectionInfo && (
              <button
                onClick={handleCollectionClick}
                className={`inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full text-xs font-medium hover:bg-indigo-100 transition-default truncate max-w-full font-body ${
                  onFilterByCollection ? 'cursor-pointer' : 'cursor-default'
                }`}
                title={`Collection: ${polish.collectionName}`}
              >
                <Sparkles className="icon-xs flex-shrink-0" />
                <span className="truncate">{polish.collectionName}</span>
              </button>
            )}
            {hasEventInfo && (
              <button
                onClick={handleEventClick}
                className={`inline-flex items-center gap-1 px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full text-xs font-medium hover:bg-purple-100 transition-default truncate max-w-full font-body ${
                  onFilterByEvent ? 'cursor-pointer' : 'cursor-default'
                }`}
                title={`Event: ${_eventDisplay}`}
              >
                <Package className="icon-xs flex-shrink-0" />
                <span className="truncate">{_eventDisplay}</span>
              </button>
            )}
            {hasReleaseInfo && (() => {
              const { text, effectiveSeason } = getReleaseDateDisplay();
              return (
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                    effectiveSeason === 'Spring' ? 'bg-pink-50 text-pink-700' :
                    effectiveSeason === 'Summer' ? 'bg-yellow-50 text-yellow-700' :
                    effectiveSeason === 'Fall' ? 'bg-orange-50 text-orange-700' :
                    effectiveSeason === 'Winter' ? 'bg-blue-50 text-blue-700' :
                    'bg-gray-50 text-gray-700'
                  }`}
                  title={text}
                >
                  {effectiveSeason === 'Spring' && '🌷'}
                  {effectiveSeason === 'Summer' && '☀️'}
                  {effectiveSeason === 'Fall' && '🍂'}
                  {effectiveSeason === 'Winter' && '❄️'}
                  {!effectiveSeason && '📅'}
                  {` ${text}`}
                </span>
              );
            })()}
          </div>
        )}

        {/* Tags - Clickable */}
        {displayTags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {displayTags.map((tag, index) => (
              <button
                key={`${tag.category}-${tag.value}-${index}`}
                onClick={(e) => handleTagClick(tag.category, tag.value, e)}
                className={`px-2 py-1 ${getTagColor(tag.category)} text-white rounded text-xs font-bold transition-default font-body ${
                  onFilterByTag ? 'cursor-pointer' : 'cursor-default'
                }`}
                title={`Filter by ${tag.category}: ${tag.label}`}
              >
                {tag.label}
              </button>
            ))}
          </div>
        )}

        {/* Quantity Badge */}
        <div className="mb-2">
          <span className="inline-block px-2 py-0.5 bg-gray-200 text-gray-600 rounded text-xs font-medium font-body">
            Qty: {polish.quantity || 1}
          </span>
        </div>

        {/* Destash Status Badge */}
        {polish.isForDestash && (
        <div className="mb-2">
          <DestashStatusBadge
          itemId={polish.id}
          userId={userId}
          currentStatus={polish.destashStatus || 'available'}
          isForDestash={polish.isForDestash}
          quantity={polish.quantity || 1}
          destashQuantityAvailable={polish.destashQuantityAvailable}
          />
        </div>
        )}

        {/* Spacer to push bottom content down */}
        <div className="flex-1"></div>

        {/* USAGE BAR - Above emojis - ONLY show if NOT on wishlist */}
        {!onWishlist && (
          <div className="mb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-bold text-light-secondary font-body">
                {usageLevel}
              </span>
              <span className="text-xs font-bold text-light-secondary font-body flex items-center gap-1">
                {timesUsed}x {isFavorite && <AppIcons.Favorite className="w-3 h-3 text-yellow-500 fill-yellow-500" aria-label="Favorite" />}
              </span>
            </div>
            <div className={`w-full h-2 rounded-full ${usageColor} overflow-hidden`}>
              <div 
                className={`h-full ${usageBarColor} transition-slow rounded-full`}
                style={{ width: `${usagePercentage}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* Horizontal Line */}
        <div className="border-t border border-gray-300 mb-3"></div>

        {/* ICON TOGGLES - At bottom, centered - 4 TOGGLES with Lucide icons! */}
        <div className="flex items-center justify-center gap-4">
          {/* PublicWishlist */}
          <button
            onClick={(e) => handleToggle('onWishlist', e)}
            disabled={isSelectionMode}
            className={`transition-all transform hover:scale-125 ${
              isSelectionMode ? 'opacity-40 cursor-not-allowed' : ''
            } ${
              onWishlist ? 'text-amber-500' : 'text-light-muted opacity-40'
            }`}
            title="PublicWishlist"
            aria-label="PublicWishlist"
          >
            <AppIcons.Wishlist className={`w-6 h-6 text-current ${onWishlist ? 'fill-amber-500 text-amber-500' : ''}`} />
          </button>

          {/* Favorite */}
          <button
            onClick={(e) => handleToggle('isFavorite', e)}
            disabled={isSelectionMode}
            className={`transition-all transform hover:scale-125 ${
              isSelectionMode ? 'opacity-40 cursor-not-allowed' : ''
            } ${
              isFavorite ? 'text-pink-500' : 'text-light-muted opacity-40'
            }`}
            title="Favorite"
            aria-label="Favorite"
          >
            <AppIcons.Favorite className={`w-6 h-6 text-current ${isFavorite ? 'fill-pink-500' : ''}`} />
          </button>

          {/* Rare/LE */}
          <button
            onClick={(e) => handleToggle('isRareLE', e)}
            disabled={isSelectionMode}
            className={`transition-all transform hover:scale-125 ${
              isSelectionMode ? 'opacity-40 cursor-not-allowed' : ''
            } ${
              isRareLE ? 'text-purple-500' : 'text-light-muted opacity-40'
            }`}
            title="Rare/Limited Edition"
            aria-label="Rare/Limited Edition"
          >
            <AppIcons.RareLE className={`w-6 h-6 ${isRareLE ? 'fill-purple-500' : ''}`} />
          </button>

          {/* Needs Info */}
          <button
            onClick={(e) => handleToggle('needsInfo', e)}
            disabled={isSelectionMode}
            className={`transition-all transform hover:scale-125 ${
              isSelectionMode ? 'opacity-40 cursor-not-allowed' : ''
            } ${
              needsInfo ? 'text-blue-500' : 'text-light-muted opacity-40'
            }`}
            title="Needs Info"
            aria-label="Needs Info"
          >
            <AppIcons.NeedsInfo className={`w-6 h-6 text-current ${needsInfo ? 'fill-blue-500' : ''}`} />
          </button>

          {/* Swatched */}
          <button
            onClick={(e) => handleToggle('swatched', e)}
            disabled={isSelectionMode}
            className={`transition-all transform hover:scale-125 ${
              isSelectionMode ? 'opacity-40 cursor-not-allowed' : ''
            } ${
              swatched ? 'text-teal-500' : 'text-light-muted opacity-40'
            }`}
            title="Swatched"
            aria-label="Swatched"
          >
            <Eye className={`w-6 h-6 ${swatched ? 'text-teal-500' : ''}`} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PolishCard;
